import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Paper, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, InputLabel,
  FormControl, Snackbar, Alert, CircularProgress, InputAdornment,
  Tooltip, IconButton, Grid, Divider, useMediaQuery, Stack, ToggleButton, ToggleButtonGroup
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Plus, X, Save, Search, Trash2, Edit3, Eye, Building,
  Bold, Italic, Underline as UnderlineIconLucide, Strikethrough, Link2, Image as ImageIcon, List, ListOrdered, Quote, Pilcrow,
  AlignLeft, AlignCenter, AlignRight, AlignJustify, Highlighter, Code2 as CodeBlockIcon, Minus as HorizontalRuleIcon, Undo2, Redo2
} from 'lucide-react';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Highlight from '@tiptap/extension-highlight';

import { format, parseISO } from 'date-fns';
import api from '../services/api';

const MenuBar = ({ editor, onImageError }) => {
  const theme = useTheme();

  const addImageFromUrl = useCallback(() => {
    if (!editor) return;
    const url = window.prompt('Paste the image URL (from Imgur or another host):');
    if (url) {
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        if (onImageError) {
          onImageError("Invalid image URL. It must start with http:// or https://");
        }
        return;
      }
      try {
        editor.chain().focus().setImage({ src: url }).run();
      } catch (error) {
        console.error("Error inserting image via URL in Tiptap:", error);
        if (onImageError) {
          onImageError("Could not insert the image. Check the URL and the console.");
        }
      }
    }
  }, [editor, onImageError]);

  const setLink = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('Link URL:', previousUrl);
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }, [editor]);

  const handleAlignment = (alignment) => {
    editor.chain().focus().setTextAlign(alignment).run();
  };

  const currentAlignment = () => {
    if (editor.isActive({ textAlign: 'left' })) return 'left';
    if (editor.isActive({ textAlign: 'center' })) return 'center';
    if (editor.isActive({ textAlign: 'right' })) return 'right';
    if (editor.isActive({ textAlign: 'justify' })) return 'justify';
    return 'left';
  };

  if (!editor) {
    return null;
  }

  const iconButtonStyle = {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '4px',
    padding: '4px',
    margin: '2px',
    color: theme.palette.text.secondary,
    '&.is-active': {
      backgroundColor: theme.palette.action.selected,
      color: theme.palette.primary.main,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    }
  };

  const toggleButtonStyle = {
    padding: '4px 8px',
    margin: '2px',
    border: `1px solid ${theme.palette.divider} !important`,
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      backgroundColor: `${theme.palette.action.selected} !important`,
      color: `${theme.palette.primary.main} !important`,
    },
    '&.Mui-selected:hover': {
      backgroundColor: `${theme.palette.action.hover} !important`,
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    }
  };

  return (
    <>
      <Paper elevation={0} sx={{ display: 'flex', flexWrap: 'wrap', p: 0.5, mb: 1, border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
        <Tooltip title="Undo"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}><Undo2 size={18} /></IconButton></Tooltip>
        <Tooltip title="Redo"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}><Redo2 size={18} /></IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '4px', height: '28px' }} />
        <Tooltip title="Bold"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''}><Bold size={18} /></IconButton></Tooltip>
        <Tooltip title="Italic"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''}><Italic size={18} /></IconButton></Tooltip>
        <Tooltip title="Underline"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleUnderline().run()} className={editor.isActive('underline') ? 'is-active' : ''}><UnderlineIconLucide size={18} /></IconButton></Tooltip>
        <Tooltip title="Strikethrough"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleStrike().run()} className={editor.isActive('strike') ? 'is-active' : ''}><Strikethrough size={18} /></IconButton></Tooltip>
        <Tooltip title="Highlight"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleHighlight().run()} className={editor.isActive('highlight') ? 'is-active' : ''}><Highlighter size={18} /></IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '4px', height: '28px' }} />
        <Tooltip title="Paragraph"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().setParagraph().run()} className={editor.isActive('paragraph') ? 'is-active' : ''}><Pilcrow size={18} /></IconButton></Tooltip>
        <Tooltip title="Heading 1"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}>H1</IconButton></Tooltip>
        <Tooltip title="Heading 2"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}>H2</IconButton></Tooltip>
        <Tooltip title="Heading 3"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}>H3</IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '4px', height: '28px' }} />
        <ToggleButtonGroup value={currentAlignment()} exclusive onChange={(event, newAlignment) => { if (newAlignment) handleAlignment(newAlignment);}} aria-label="text alignment" size="small" sx={{ margin: '2px' }}>
          <Tooltip title="Align Left"><ToggleButton value="left" aria-label="left aligned" sx={toggleButtonStyle}><AlignLeft size={18} /></ToggleButton></Tooltip>
          <Tooltip title="Align Center"><ToggleButton value="center" aria-label="centered" sx={toggleButtonStyle}><AlignCenter size={18} /></ToggleButton></Tooltip>
          <Tooltip title="Align Right"><ToggleButton value="right" aria-label="right aligned" sx={toggleButtonStyle}><AlignRight size={18} /></ToggleButton></Tooltip>
          <Tooltip title="Justify"><ToggleButton value="justify" aria-label="justified" sx={toggleButtonStyle}><AlignJustify size={18} /></ToggleButton></Tooltip>
        </ToggleButtonGroup>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '4px', height: '28px' }} />
        <Tooltip title="Bullet List"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleBulletList().run()} className={editor.isActive('bulletList') ? 'is-active' : ''}><List size={18} /></IconButton></Tooltip>
        <Tooltip title="Numbered List"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleOrderedList().run()} className={editor.isActive('orderedList') ? 'is-active' : ''}><ListOrdered size={18} /></IconButton></Tooltip>
        <Tooltip title="Blockquote"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleBlockquote().run()} className={editor.isActive('blockquote') ? 'is-active' : ''}><Quote size={18} /></IconButton></Tooltip>
        <Tooltip title="Code Block"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().toggleCodeBlock().run()} className={editor.isActive('codeBlock') ? 'is-active' : ''}><CodeBlockIcon size={18} /></IconButton></Tooltip>
        <Tooltip title="Horizontal Rule"><IconButton sx={iconButtonStyle} onClick={() => editor.chain().focus().setHorizontalRule().run()}><HorizontalRuleIcon size={18} /></IconButton></Tooltip>
        <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: '4px', height: '28px' }} />
        <Tooltip title="Add Link"><IconButton sx={iconButtonStyle} onClick={setLink} className={editor.isActive('link') ? 'is-active' : ''}><Link2 size={18} /></IconButton></Tooltip>
        <Tooltip title="Add Image via URL"><IconButton sx={iconButtonStyle} onClick={addImageFromUrl}><ImageIcon size={18} /></IconButton></Tooltip>
      </Paper>
    </>
  );
};


const TutorialPage = () => {
  const [currentUserRole, setCurrentUserRole] = useState(1);
  const [tutorials, setTutorials] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContentLoading, setDialogContentLoading] = useState(false);
  const [isViewMode, setIsViewMode] = useState(false);
  const [editingTutorial, setEditingTutorial] = useState(null);
  const [formTitle, setFormTitle] = useState('');
  const [formHotelId, setFormHotelId] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const searchTimeoutRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
      }),
      Image.configure({
        inline: false,
      }),
      Placeholder.configure({
        placeholder: 'Write the tutorial content here...',
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Highlight.configure({ multicolor: true }),
    ],
    content: '',
    editable: true,
  });

  useEffect(() => {
    if (editor) {
      if (dialogOpen && !isViewMode) {
        editor.setEditable(true);
        if (editor.getHTML() !== (editingTutorial?.content || '')) {
            editor.commands.setContent(editingTutorial?.content || '');
        }
      } else if (dialogOpen && isViewMode) {
        editor.setEditable(false);
        if (editor.getHTML() !== (editingTutorial?.content || '<p><em>Content not available.</em></p>')) {
            editor.commands.setContent(editingTutorial?.content || '<p><em>Content not available.</em></p>');
        }
      } else {
        editor.setEditable(false);
      }
    }
  }, [dialogOpen, isViewMode, editingTutorial, editor]);


  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleImageError = (errorMessage) => {
    showSnackbar(errorMessage, 'error');
  };

  const fetchHotels = useCallback(async () => {
    try {
      const response = await api.get('/hotels-tutorial');
      setHotels(response.data || []);
    } catch (error) {
      console.error("Error fetching hotels:", error);
      showSnackbar("Error loading hotel list.", "error");
    }
  }, []);

  const fetchTutorials = useCallback(async (currentSearch = searchTerm, showLoadingIndicator = true) => {
    if (showLoadingIndicator) setLoading(true);
    try {
      const response = await api.get('/tutorials');
      let fetchedTutorials = response.data || [];

      if (currentSearch) {
        fetchedTutorials = fetchedTutorials.filter(tut =>
          tut.title.toLowerCase().includes(currentSearch.toLowerCase()) ||
          (tut.hotel_name && tut.hotel_name.toLowerCase().includes(currentSearch.toLowerCase()))
        );
      }

      setTutorials(fetchedTutorials);

    } catch (error) {
      console.error('Error fetching tutorials:', error);
      showSnackbar('Error loading tutorials.', 'error');
      setTutorials([]);
    } finally {
      if (showLoadingIndicator) setLoading(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    const userFromAuth = { role_id: 2 };
    setCurrentUserRole(userFromAuth.role_id);
    fetchHotels();
  }, [fetchHotels]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      fetchTutorials(searchTerm);
    }, 500);
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, [searchTerm, fetchTutorials]);


  const handleOpenDialog = async (tutorial = null, viewOnly = false) => {
    setIsViewMode(viewOnly);
    setDialogContentLoading(false);

    if (tutorial) {
      setEditingTutorial(tutorial);
      setFormTitle(tutorial.title);
      setFormHotelId(tutorial.hotel_id || '');
      setDialogOpen(true);

      setDialogContentLoading(true);
      try {
        const response = await api.get(`/tutorials/${tutorial.id}`);
        const fullTutorialData = response.data;
        setEditingTutorial(fullTutorialData);
        if (editor) editor.commands.setContent(fullTutorialData.content || '');
        setFormTitle(fullTutorialData.title);
      } catch (error) {
        console.error("Error fetching full tutorial details:", error);
        showSnackbar("Error loading tutorial details.", "error");
        if (editor) editor.commands.setContent(tutorial.content || '');
      } finally {
        setDialogContentLoading(false);
      }
    } else {
      setEditingTutorial(null);
      setFormTitle('');
      setFormHotelId('');
      if (editor) editor.commands.setContent('');
      setDialogOpen(true);
    }
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTutorial(null);
    setFormTitle('');
    setFormHotelId('');
    if (editor) editor.commands.setContent('');
  };

  const handleSaveTutorial = async () => {
    if (!editor || !formTitle.trim()) {
      showSnackbar("Title is required and the editor must be ready.", "warning");
      return;
    }
    const htmlContent = editor.getHTML();
    if (!htmlContent || htmlContent === '<p></p>') {
      showSnackbar("Content is required.", "warning");
      return;
    }

    const tutorialData = {
      title: formTitle.trim(),
      content: htmlContent,
      hotel_id: formHotelId ? parseInt(formHotelId, 10) : null,
    };

    setLoading(true);
    try {
      if (editingTutorial && editingTutorial.id) {
        await api.put(`/tutorials/${editingTutorial.id}`, tutorialData);
        showSnackbar("Tutorial updated successfully!", "success");
      } else {
        await api.post('/tutorials', tutorialData);
        showSnackbar("Tutorial created successfully!", "success");
      }
      fetchTutorials(searchTerm, false);
      handleCloseDialog();
    } catch (error) {
      console.error("Error saving tutorial:", error);
      showSnackbar(error.response?.data?.msg || "Error saving tutorial.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTutorial = async (tutorialId) => {
    if (!window.confirm("Are you sure you want to delete this tutorial? This action cannot be undone.")) return;

    setLoading(true);
    try {
      await api.delete(`/tutorials/${tutorialId}`);
      showSnackbar("Tutorial deleted successfully!", "success");
      fetchTutorials(searchTerm, false);
    } catch (error) {
      console.error("Error deleting tutorial:", error);
      showSnackbar(error.response?.data?.msg || "Error deleting tutorial.", "error");
    } finally {
      setLoading(false);
    }
  };

  const canEdit = currentUserRole === 2 || currentUserRole === 3;

  return (
    <Box p={1}>
      <Box sx={{ pb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', gap: 1, maxWidth: 1000, margin: '0 auto' }} >
        <TextField
          fullWidth
          size="small"
          placeholder="Search tutorials..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          variant="outlined"
          InputProps={{
            startAdornment: (<InputAdornment position="start"><Search size={18} color={theme.palette.text.secondary} /></InputAdornment>),
            endAdornment: searchTerm ? (<InputAdornment position="end"><IconButton onClick={() => setSearchTerm('')} size="small" edge="end"><X size={16} /></IconButton></InputAdornment>) : null,
          }}
          sx={{
            flexGrow: 1,
            backgroundColor: '#fff',
            borderRadius: theme.shape.borderRadius * 5 / (isMobile ? 2 : 1) + 'px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            '& .MuiOutlinedInput-root': {
              borderRadius: 'inherit',
              '& fieldset': { border: 'none' },
            },
          }}
        />
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<Plus size={18} />}
            onClick={() => handleOpenDialog(null, false)}
            sx={{
              height: '40px',
              width: { xs: '100%', sm: 'auto' },
              minWidth: { sm: '180px' },
              background: 'linear-gradient(to right, #1976d2, #0d47a1)',
              color: '#fff',
              fontWeight: 'bold',
              '&:hover': {
                background: 'linear-gradient(to right, #1565c0, #003c8f)',
              },
              flexShrink: 0,
            }}
          >
            NEW TUTORIAL
          </Button>
        )}
      </Box>

      {loading && tutorials.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}><CircularProgress size={50} /></Box>
      ) : !loading && tutorials.length === 0 && !searchTerm ? (
        <Typography sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>No tutorials found. {canEdit && "Click 'New Tutorial' to get started."}</Typography>
      ) : !loading && tutorials.length === 0 && searchTerm ? (
        <Typography sx={{ textAlign: 'center', mt: 3, color: 'text.secondary' }}>No tutorials found for "{searchTerm}".</Typography>
      ) : (
        <Box sx={{ overflowX: 'auto', maxWidth: 1000, margin: '0 auto', width: '100%' }}>
          <Paper sx={{ backgroundColor: '#fff', borderRadius: 2, boxShadow: 3, overflow: 'hidden' }}>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 200px)' }}>
              <Table stickyHeader size="small">
                <TableHead><TableRow sx={{ '& th': { backgroundColor: '#f0f0f0', fontWeight: 'bold', whiteSpace: 'nowrap' } }}>
                  <TableCell>Title</TableCell><TableCell>Hotel</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Updated at</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {tutorials.map((tutorial) => (
                    <TableRow hover key={tutorial.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' } }}>
                      <TableCell component="th" scope="row" sx={{ fontWeight: 500, maxWidth: 250, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Tooltip title={tutorial.title} placement="top-start"><span>{tutorial.title}</span></Tooltip>
                      </TableCell>
                      <TableCell>{tutorial.hotel_name || 'ALL'}</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{tutorial.updated_at ? format(parseISO(tutorial.updated_at), 'dd/MM/yy HH:mm') : '-'}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="View"><IconButton size="small" onClick={() => handleOpenDialog(tutorial, true)} color="info"><Eye size={18} /></IconButton></Tooltip>
                        {canEdit && (<>
                          <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpenDialog(tutorial, false)} color="primary"><Edit3 size={18} /></IconButton></Tooltip>
                          <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDeleteTutorial(tutorial.id)} color="error"><Trash2 size={18} /></IconButton></Tooltip>
                        </>)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth={isViewMode ? "md" : "lg"} fullWidth scroll="paper">
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${theme.palette.divider}` }}>
          {isViewMode ? `${editingTutorial?.title || ''}` : (editingTutorial ? 'Edit Tutorial' : 'New Tutorial')}
          <IconButton onClick={handleCloseDialog} size="small"><X size={20} /></IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ minHeight: isViewMode ? 'auto' : '400px', display: 'flex', flexDirection: 'column' }}>
          {dialogContentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1, minHeight: '200px' }}>
              <CircularProgress />
            </Box>
          ) : isViewMode ? (
            <Box sx={{
              py: 2,
              '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
              '& p': { lineHeight: 1.6, marginBlockStart: '0.5em', marginBlockEnd: '0.5em' },
              '& h1, & h2, & h3, & h4, & h5, & h6': { marginBlockStart: '0.1em', marginBlockEnd: '0.4em', lineHeight: 1 }
            }}>
              {editingTutorial?.hotel_name && (
                <Typography variant="subtitle1" color="textSecondary" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <Building size={16} style={{ marginRight: theme.spacing(0.5) }} /> Hotel: {editingTutorial.hotel_name}
                </Typography>
              )}
              <Divider sx={{ my: 1 }} />
              <Box dangerouslySetInnerHTML={{ __html: editingTutorial?.content || '<p><em>Content not available.</em></p>' }} />
            </Box>
          ) : (
            <Box component="form" noValidate autoComplete="off" sx={{ pt: 1, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <Grid container spacing={2} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={8}><TextField fullWidth required label="Tutorial Title" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} variant="outlined" size="small" /></Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth size="small" variant="outlined">
                    <InputLabel id="hotel-select-label">Hotel (Optional)</InputLabel>
                    <Select labelId="hotel-select-label" value={formHotelId} onChange={(e) => setFormHotelId(e.target.value)} label="Hotel (Optional)">
                      <MenuItem value=""><em>ALL</em></MenuItem>
                      {hotels.map((hotel) => (<MenuItem key={hotel.id} value={hotel.id}>{hotel.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              {editor && <MenuBar editor={editor} onImageError={handleImageError} />}
              <Paper
                variant="outlined"
                sx={{
                  borderColor: theme.palette.grey[400],
                  borderRadius: 1,
                  overflow: 'hidden',
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  '& .ProseMirror': {
                    flexGrow: 1,
                    overflowY: 'auto',
                    padding: '8px 12px',
                    minHeight: '250px',
                    lineHeight: '1.5',
                    '& p': { marginTop: '0.2em', marginBottom: '0.4em' },
                    '& h1, & h2, & h3, & h4, & h5, & h6': { marginTop: '0.6em', marginBottom: '0.3em', lineHeight: '1.3' },
                    '& ul, & ol': { marginTop: '0.3em', marginBottom: '0.3em', paddingLeft: '2em' },
                    '& li > p': { marginTop: '0.1em', marginBottom: '0.1em' },
                    '& blockquote': { borderLeft: `3px solid ${theme.palette.grey[300]}`, marginLeft: '0.5em', paddingLeft: '1em', fontStyle: 'italic', color: theme.palette.text.secondary },
                    '& pre': { backgroundColor: theme.palette.grey[100], borderRadius: '4px', padding: '0.8em 1em', overflowX: 'auto', fontFamily: 'monospace' },
                    '& hr': { border: 'none', borderTop: `1px solid ${theme.palette.divider}`, margin: '1em 0' }
                  }
                }}
              >
                <EditorContent editor={editor} />
              </Paper>
            </Box>
          )}
        </DialogContent>

        {!isViewMode && (
          <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button onClick={handleCloseDialog} color="secondary" variant="outlined">CANCEL</Button>
            <Button onClick={handleSaveTutorial} color="primary" variant="contained" startIcon={<Save size={18} />} disabled={loading || !editor?.isEditable || dialogContentLoading}>
              {loading ? <CircularProgress size={20} color="inherit" /> : (editingTutorial ? 'SAVE UPDATE' : 'CREATE NEW')}
            </Button>
          </DialogActions>
        )}
        {isViewMode && !dialogContentLoading && (
          <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
            <Button onClick={handleCloseDialog} color="primary" variant="outlined">Close</Button>
          </DialogActions>
        )}
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} severity={snackbar.severity} sx={{ width: '100%', boxShadow: 6 }} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default TutorialPage;